/**
 * SlideSheet — RTL-safe slide-in panel for tafsir, word details, etc.
 *
 * Key motion correctness:
 *   • Slides on `inset-inline-end` axis (logical property) — automatically
 *     flips in RTL (Arabic locale) without extra JS.
 *   • Dismiss waits for `transitionend` event on the sheet element — no
 *     hardcoded `setTimeout(fn, 300)` that would de-sync if the duration
 *     token changes.
 *   • Transition: `inset-inline-end var(--motion-duration-slow) var(--motion-easing-decelerate)`
 *
 * Usage:
 *   const sheet = new SlideSheet({ title: 'Tafsir' });
 *   sheet.setContent('<p>…</p>');
 *   sheet.open();
 *   // later:
 *   sheet.close();          // animate-out, then self-remove
 *   // or:
 *   sheet.destroy();        // immediate remove (e.g. page navigation)
 */

let _stylesInjected = false;

function injectStyles(): void {
  if (_stylesInjected || document.getElementById('hisni-slide-sheet-styles')) return;
  _stylesInjected = true;
  const s = document.createElement('style');
  s.id = 'hisni-slide-sheet-styles';
  s.textContent = `
    .hss-overlay {
      position: fixed;
      inset: 0;
      z-index: 300;
      background: rgba(0, 0, 0, .45);
      opacity: 0;
      transition: opacity var(--motion-duration-slow, 400ms) var(--motion-easing-decelerate, ease-out);
    }
    .hss-overlay.hss-overlay--visible {
      opacity: 1;
    }

    .hss-sheet {
      position: fixed;
      inset-block: 0;
      inset-inline-end: calc(-1 * min(480px, 100vw));
      inline-size: min(480px, 100vw);
      z-index: 301;
      display: flex;
      flex-direction: column;
      background: var(--ln-bg-surface, #fff);
      box-shadow: -4px 0 24px rgba(0, 0, 0, .18);
      transition: inset-inline-end var(--motion-duration-slow, 400ms) var(--motion-easing-decelerate, ease-out);
      overflow: hidden;
    }
    /* Slide in: move inset-inline-end to 0 */
    .hss-sheet.hss-sheet--open {
      inset-inline-end: 0;
    }

    .hss-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      border-bottom: 1px solid var(--ln-border, rgba(0,0,0,.08));
      flex-shrink: 0;
    }
    .hss-title {
      font-size: 1rem;
      font-weight: 700;
      color: var(--ln-text-primary, #0F172A);
    }
    .hss-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      background: transparent;
      cursor: pointer;
      color: var(--ln-text-secondary, #64748B);
      font-size: 1.2rem;
      transition: background var(--motion-duration-fast, 120ms) ease;
    }
    .hss-close:hover { background: var(--ln-bg-subtle, rgba(0,0,0,.06)); }
    .hss-close:focus-visible { outline: 2px solid var(--ln-accent, #0A0A0A); outline-offset: 2px; }

    .hss-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      overscroll-behavior: contain;
    }

    .dark .hss-sheet {
      background: var(--ln-bg-card, #1E293B);
      box-shadow: -4px 0 24px rgba(0, 0, 0, .5);
    }

    @media (prefers-reduced-motion: reduce) {
      .hss-overlay { transition: none; }
      .hss-sheet { transition: none; }
    }
  `;
  document.head.appendChild(s);
}

export interface SlideSheetOptions {
  title?: string;
  /** Close when user clicks the backdrop overlay. Default: true. */
  closeOnOverlay?: boolean;
  /** Called after the sheet is fully closed and removed from DOM. */
  onClose?: () => void;
}

export class SlideSheet {
  private overlayEl: HTMLElement;
  private sheetEl: HTMLElement;
  private bodyEl: HTMLElement;
  private titleEl: HTMLElement;
  private _isOpen = false;
  private readonly opts: Required<SlideSheetOptions>;

  constructor(opts: SlideSheetOptions = {}) {
    injectStyles();

    this.opts = {
      title: opts.title ?? '',
      closeOnOverlay: opts.closeOnOverlay ?? true,
      onClose: opts.onClose ?? (() => undefined),
    };

    // Overlay (backdrop)
    this.overlayEl = document.createElement('div');
    this.overlayEl.className = 'hss-overlay';
    this.overlayEl.setAttribute('aria-hidden', 'true');

    // Sheet panel
    this.sheetEl = document.createElement('div');
    this.sheetEl.className = 'hss-sheet';
    this.sheetEl.setAttribute('role', 'dialog');
    this.sheetEl.setAttribute('aria-modal', 'true');
    this.sheetEl.setAttribute('aria-label', this.opts.title || 'Panel');

    const closeBtn = document.createElement('button');
    closeBtn.className = 'hss-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    this.titleEl = document.createElement('span');
    this.titleEl.className = 'hss-title';
    this.titleEl.textContent = this.opts.title;

    const header = document.createElement('div');
    header.className = 'hss-header';
    header.appendChild(this.titleEl);
    header.appendChild(closeBtn);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'hss-body';

    this.sheetEl.appendChild(header);
    this.sheetEl.appendChild(this.bodyEl);

    // Wire events
    closeBtn.addEventListener('click', () => this.close());
    if (this.opts.closeOnOverlay) {
      this.overlayEl.addEventListener('click', () => this.close());
    }

    // Trap focus: close on Escape
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') this.close();
  }

  /** Replace the sheet body content. */
  setContent(html: string): void {
    this.bodyEl.innerHTML = html;
  }

  /** Update the panel title. */
  setTitle(title: string): void {
    this.titleEl.textContent = title;
    this.sheetEl.setAttribute('aria-label', title || 'Panel');
  }

  /** Append the sheet to `document.body` (or given parent) and animate in. */
  open(parent: HTMLElement = document.body): void {
    if (this._isOpen) return;
    this._isOpen = true;

    parent.appendChild(this.overlayEl);
    parent.appendChild(this.sheetEl);
    document.addEventListener('keydown', this._onKeyDown);

    // Trigger animations in next frame so CSS transitions run
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlayEl.classList.add('hss-overlay--visible');
        this.sheetEl.classList.add('hss-sheet--open');
      });
    });
  }

  /**
   * Animate the sheet out, then remove it from the DOM.
   *
   * Uses `transitionend` on the sheet element — accurate regardless of
   * whatever duration the `--motion-duration-slow` token resolves to.
   * No `setTimeout(fn, 300)` hardcoded literal.
   */
  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;

    document.removeEventListener('keydown', this._onKeyDown);

    this.overlayEl.classList.remove('hss-overlay--visible');
    this.sheetEl.classList.remove('hss-sheet--open');

    // Remove overlay and sheet only after the sheet transition completes.
    // Guard ensures onClose fires exactly once even if transitionend and the
    // safeguard timer both try to run.
    let _cleaned = false;
    const cleanup = () => {
      if (_cleaned) return;
      _cleaned = true;
      clearTimeout(safeguard);
      this.overlayEl.remove();
      this.sheetEl.remove();
      this.opts.onClose();
    };
    this.sheetEl.addEventListener('transitionend', cleanup, { once: true });

    // Safety fallback: if transitionend never fires (e.g. display:none parent),
    // remove after a generous grace period
    const safeguard = setTimeout(cleanup, 1000);
  }

  /**
   * Remove immediately without animation (use on page navigation / module unmount).
   */
  destroy(): void {
    document.removeEventListener('keydown', this._onKeyDown);
    this._isOpen = false;
    this.overlayEl.remove();
    this.sheetEl.remove();
  }
}
